import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { studentShortQuestionAPI } from "../../redux/features/courseSlice";
import AssignmentTimerCompleteModal from "../assignmentTimerCompleteModal/AssignmentTimerCompleteModal";
import AssignmentConfirmSubmitModal from "../AssignmentConfirmSubmitModal/AssignmentConfirmSubmitModal";

const ComprehensiveQuestionAssignment = ({ data }) => {
  const [questions, setQuestions] = useState([]);
  const [counter, setCounter] = useState(0);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [showTimerModal, setShowTimerModal] = useState(false);
  const [timerStarted, setTimerStarted] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const [fieldDisabled, setFieldDisabled] = useState(false);


  // Initialize initial values
  const initialMarksArray =
    data?.questions?.map((question) => ({
      question_id: question?.id || "",
      user_answer: "",
    })) || [];

  // Initialize formik before useEffect hooks
  const formik = useFormik({
    initialValues: {
      assignmentId: searchParams.get("id") || "",
      marksArray: initialMarksArray,
    },
    // validationSchema,
    enableReinitialize: true,
    onSubmit: (values, { setSubmitting, setStatus }) => {
      handleSubmit(values, { setSubmitting, setStatus });
    },
  });

  const handleSubmit = async (values, { setSubmitting, setStatus }) => {
    try {
      const res = await dispatch(
        studentShortQuestionAPI({
          token: auth.token,
          assignment_Id: searchParams.get("id"),
          data: values.marksArray,
        })
      ).unwrap();
      if (res.success) {
        toast.success(res?.message, {
          position: "top-center",
        });
        setFormSubmitted(true);
      }
    } catch (error) {
      console.error(error);
      setStatus({ error: "Failed to submit answers. Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (searchParams.get("view") === "true") {
      setFieldDisabled(true);
    } else {
      setFieldDisabled(false);
    }
  }, []);

  // Initialize questions
  useEffect(() => {
    setQuestions(data?.questions || []);
  }, [data]);

  // Initialize timer
  useEffect(() => {
    if (data?.duration) {
      setCounter(data.duration * 60);
      setTimerStarted(true);
    } else {
      setTimerStarted(false);
    }
  }, [data?.duration]);

  // Timer countdown
  useEffect(() => {
    if (searchParams.get("view") === "true" || !timerStarted) {
      return;
    }

    const interval = setInterval(() => {
      setCounter((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          if (!formSubmitted) {
            console.log("Time's up! Auto-submitting...");
            formik.handleSubmit(); // Auto-submit when timer reaches zero
            setShowTimerModal(true); // Show modal after submission
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [timerStarted, formSubmitted]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, "0")}:${remainingSeconds
      .toString()
      .padStart(2, "0")} min`;
  };

  const handleTimerContinue = () => {
    setShowTimerModal(false);
    navigate(-1);
  };

  const handleSubmitConfirm = () => {
    setShowSubmitModal(false);
    formik.handleSubmit();
    navigate(-1);
  };

  const handleSubmitCancel = () => {
    setShowSubmitModal(false);
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    setShowSubmitModal(true);
  };

  const goBack = () => {
    navigate(-1);
  };

  return (
    <>
      <div className="assignment-header mb-10 mb-sm-20 mb-md-24">
        <h3 className="fw-bold ah-heading-04">{data?.title}</h3>
        <p>{data?.description}</p>
        <div className="item ah-text-md fw-bold theme-color text-end">
          Duration:{" "}
          <span className="fw-semibold black-100 opacity-50">
            {formatTime(counter)}
          </span>
        </div>
      </div>

      <div className="card-default-style px-15 px-sm-25 px-md-35">
        <div className="assignment">
          <div
            className="assignment-inner overflow-hidden"
            style={{ maxHeight: "100%" }}
          >
            <h4 className="fw-bold ah-text-md black-100 mb-24">
              1. {data?.passage}
            </h4>
            {initialMarksArray.length > 0 && (
              <form onSubmit={handleManualSubmit}>
                {formik.status?.success && (
                  <div className="alert alert-success">
                    {formik.status.success}
                  </div>
                )}
                {formik.status?.error && (
                  <div className="alert alert-danger">
                    {formik.status.error}
                  </div>
                )}

                <ul className="p-0 list-unstyled mb-10 mb-sm-20 mb-md-32">
                  {questions.map((question, index) => (
                    <li
                      key={question.id}
                      className="mb-18 d-flex align-items-start justify-content-between flex-column-reverse flex-sm-row gap-2 gap-lg-3 gap-xl-5"
                    >
                      <div className="question w-100">
                        <input
                          type="hidden"
                          name={`marksArray.${index}.question_id`}
                          value={question.id}
                          onChange={formik.handleChange}
                        />
                        <label
                          htmlFor={`marksArray.${index}.user_answer`}
                          className="form-label fw-bold ah-text-sm black-100 mb-10"
                        >
                          {index + 1}. {question?.question_text}
                        </label>

                        <textarea
                          className="form-control"
                          name={`marksArray.${index}.user_answer`}
                          value={formik.values.marksArray[index]?.user_answer || ""}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          disabled={fieldDisabled}
                          rows={3}
                          placeholder="Write Answer..."
                        />
                        {formik.errors.marksArray &&
                          formik.errors.marksArray[index]?.user_answer &&
                          formik.touched.marksArray &&
                          formik.touched.marksArray[index]?.user_answer && (
                            <div className="text-danger d-block">
                              {formik.errors.marksArray[index].user_answer}
                            </div>
                          )}
                      </div>

                      <span className="points flex-shrink-0 h-auto px-29 py-8 ah-text-xs fw-semibold">
                        {question.marks || 1} Point
                        {question.marks !== 1 ? "s" : ""}
                      </span>
                    </li>
                  ))}
                </ul>

                <div className="mt-20">
                  {fieldDisabled ? (
                    <button
                      className="btn btn-primary no-shadow px-50 py-15"
                      type="button"
                      onClick={goBack}
                    >
                      Back
                    </button>
                  ) : (
                    <button
                      className="btn btn-primary no-shadow px-50 py-15"
                      type="submit"
                      disabled={formik.isSubmitting}
                    >
                      {formik.isSubmitting ? "Submitting..." : "Submit"}
                    </button>
                  )}
                </div>
              </form>
            )}
            <AssignmentTimerCompleteModal
              show={showTimerModal}
              onContinue={handleTimerContinue}
            />
            <AssignmentConfirmSubmitModal
              show={showSubmitModal}
              onConfirm={handleSubmitConfirm}
              onCancel={handleSubmitCancel}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default ComprehensiveQuestionAssignment;
